import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Play, Sparkles, Users, Compass, Activity } from "lucide-react";
import Stats from "../components/sections/Stats";
import PartnersMarquee from "../components/sections/PartnersMarquee";
import Testimonials from "../components/sections/Testimonials";

const FOUNDER_VIDEO_URL =
  "https://customer-assets-agu9un31.emergentagent.net/job_deployedd-labs/artifacts/vrcnhqrm_gemini_generated_video_21a13730.mp4";

const pillars = [
  { letter: "K", title: "Knowledge", desc: "Strong foundations through structured learning, equipping individuals with the skills required to perform in real-world environments.", icon: Sparkles },
  { letter: "I", title: "Innovation", desc: "Industry projects, startup cohorts and guided mentorship — enabling individuals to experiment, build, and solve real problems.", icon: Compass },
  { letter: "A", title: "Acceleration", desc: "Capability into opportunity through placements, deployments, and CFT-led ventures with real investment-backed execution.", icon: Activity },
];

const ecosystem = [
  { tag: "01", title: "Capability Building", desc: "Outcome-driven learning experiences that prepare individuals for real-world performance." },
  { tag: "02", title: "Talent Deployment", desc: "Bridging trained talent and industry demand through structured deployment models." },
  { tag: "03", title: "Deployedd Digital", desc: "Technology-enabled services that support hiring, training and workforce readiness at scale." },
  { tag: "04", title: "Centre for Sustainability & EQ", desc: "Emotional intelligence and sustainability woven into workforce development." },
];

const Home = () => {
  const [founderPlaying, setFounderPlaying] = useState(false);
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative container-wide pt-16 md:pt-24 pb-20 md:pb-28 overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8 reveal">
            <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-[#9F381A] mb-8 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-[#9F381A]" /> Deployedd Labs — Estd. for what's next
            </p>
            <h1 className="font-heading text-[14vw] md:text-[10vw] lg:text-[8.4vw] tracking-tighter leading-[0.85]">
              Knowledge.<br />
              Innovation.<br />
              <em className="font-editorial italic text-[#9F381A]">Acceleration.</em>
            </h1>
          </div>
          <div className="lg:col-span-4 reveal reveal-delay-2">
            <p className="text-lg md:text-xl leading-relaxed text-ink-muted max-w-md mb-8">
              We sit at the intersection of universities, industry and leadership — turning learning into capability, and capability into outcomes.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/services"
                data-testid="hero-cta-primary"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-6 py-3.5 text-[11px] uppercase tracking-[0.25em] hover:bg-[#9F381A] transition-colors"
              >
                The Ecosystem <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a
                href="https://wa.me/918122431120?text=Hi%20Deployedd%20Labs%20%E2%80%94%20I%27d%20like%20to%20start%20a%20conversation."
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center gap-2 border border-ink px-6 py-3.5 text-[11px] uppercase tracking-[0.25em] hover:bg-ink hover:text-white transition-colors"
              >
                Start a conversation
              </a>
            </div>
          </div>
        </div>

        {/* HERO image strip */}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-6 items-center reveal reveal-delay-3">
          <div className="md:col-span-7 relative h-[280px] md:h-[440px] overflow-hidden">
            <img
              src="https://images.pexels.com/photos/11558908/pexels-photo-11558908.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
              alt="Deployedd Labs hero"
              loading="eager"
              decoding="async"
              fetchPriority="high"
              className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-700"
            />
          </div>
          <div className="md:col-span-5 space-y-4">
            <p className="font-mono-cust text-[11px] uppercase tracking-[0.25em] text-gray-500">/ 00 — What we do, in one line</p>
            <p className="font-editorial italic text-2xl md:text-3xl leading-snug">
              Capability building through live execution, cross-functional team-based learning, direct industry integration, and outcome-driven deployment pathways.
            </p>
          </div>
        </div>
      </section>

      <Stats />

      {/* K-I-A pillars */}
      <section data-testid="kia-section" className="container-wide py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-6">/ 02 — Flagship Model</p>
            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
              The K · I · A<br />
              <em className="font-editorial italic text-[#9F381A]">talent development</em> model.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-lg text-ink-muted">
              A three-stage architecture that takes a learner from foundation to ownership. Each stage is operationally linked to the next — no dead ends, no certificate theatre.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-ink">
          {pillars.map((p) => {
            const Icon = p.icon;
            return (
              <div
                key={p.letter}
                data-testid={`kia-${p.letter.toLowerCase()}`}
                className="border-r border-b border-ink p-10 md:p-12 group hover:bg-[#F3F3F1] transition-colors duration-500 relative"
              >
                <div className="flex items-start justify-between mb-12">
                  <span className="font-heading text-[18vw] md:text-[10vw] leading-none tracking-tighter text-[#9F381A]/15 group-hover:text-[#9F381A] transition-colors">
                    {p.letter}
                  </span>
                  <Icon className="w-6 h-6 text-ink-muted group-hover:text-[#9F381A] transition-colors" />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl tracking-tight mb-4">{p.title}</h3>
                <p className="text-base text-ink-muted leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Ecosystem strip */}
      <section data-testid="ecosystem-strip" className="bg-ink text-white py-24 md:py-32 grain relative">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16 items-end">
            <div className="lg:col-span-7">
              <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-white/50 mb-6">/ 03 — The Deployedd Ecosystem</p>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
                One integrated model.<br />
                <em className="font-editorial italic text-[#E68A6E]">Four operating arms.</em>
              </h2>
            </div>
            <div className="lg:col-span-4 lg:col-start-9">
              <Link to="/services" className="inline-flex items-center gap-2 border border-white/30 px-6 py-3.5 text-[11px] uppercase tracking-[0.25em] hover:bg-white hover:text-ink transition-colors">
                Explore the ecosystem <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-l border-white/15">
            {ecosystem.map((e) => (
              <div key={e.tag} data-testid={`ecosystem-${e.tag}`} className="border-r border-t border-b border-white/15 p-8 md:p-10 hover:bg-white/5 transition-colors">
                <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-[#E68A6E] mb-8">{e.tag}</p>
                <h3 className="font-heading text-xl md:text-2xl tracking-tight mb-4">{e.title}</h3>
                <p className="text-sm text-white/65 leading-relaxed">{e.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder video */}
      <section data-testid="founder-section" className="container-wide py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="relative w-full aspect-video bg-ink overflow-hidden group">
              {!founderPlaying ? (
                <button
                  type="button"
                  onClick={() => setFounderPlaying(true)}
                  data-testid="founder-video-play"
                  aria-label="Play founder video"
                  className="absolute inset-0 w-full h-full flex items-center justify-center"
                >
                  <video
                    src={FOUNDER_VIDEO_URL}
                    className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-95 transition-opacity bg-ink"
                    muted
                    playsInline
                    preload="metadata"
                  />
                  <span className="absolute inset-0 bg-ink/25 group-hover:bg-ink/10 transition-colors" />
                  <span className="relative z-10 w-20 h-20 md:w-24 md:h-24 rounded-full bg-white flex items-center justify-center group-hover:bg-[#9F381A] group-hover:text-white transition-colors">
                    <Play className="w-7 h-7 ml-1 fill-current" />
                  </span>
                  <span className="absolute bottom-5 left-5 font-mono-cust text-[11px] uppercase tracking-[0.25em] text-white">
                    Watch · From the Founder
                  </span>
                </button>
              ) : (
                <video
                  data-testid="founder-video-player"
                  src={FOUNDER_VIDEO_URL}
                  className="w-full h-full object-contain bg-ink"
                  controls
                  autoPlay
                  playsInline
                />
              )}
            </div>
          </div>
          <div className="lg:col-span-5">
            <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-gray-500 mb-6">/ 05 — From the Founder</p>
            <h2 className="font-heading text-4xl md:text-5xl tracking-tighter leading-[0.95] mb-8">
              Why we built<br />
              <em className="font-editorial italic text-[#9F381A]">Deployedd Labs.</em>
            </h2>
            <p className="text-lg text-ink-muted mb-8 leading-relaxed">
              "We weren't trying to build another training company. We were trying to design the bridge that doesn't exist — between what universities teach, what industry actually needs, and what individuals want to own."
            </p>
            <p className="font-heading text-lg">— Founder, Deployedd Labs</p>
          </div>
        </div>
      </section>

      <PartnersMarquee />
      <Testimonials />

      {/* CTA */}
      <section data-testid="home-cta" className="container-wide py-24 md:py-32">
        <div className="bg-ink text-white p-12 md:p-20 lg:p-28 grain relative overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end relative z-10">
            <div className="lg:col-span-8">
              <p className="font-mono-cust text-[11px] uppercase tracking-[0.3em] text-[#E68A6E] mb-8 flex items-center gap-3">
                <Users className="w-3.5 h-3.5" /> Get ready to grow with Deployedd Labs
              </p>
              <h2 className="font-heading text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.9]">
                The next cohort. The next bench.<br />
                <em className="font-editorial italic text-[#E68A6E]">The next outcome.</em>
              </h2>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-4">
              <Link to="/contact" className="inline-flex items-center justify-between bg-white text-ink px-6 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-[#9F381A] hover:text-white transition-colors">
                Talk to us <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/pulse" className="inline-flex items-center justify-between border border-white/30 px-6 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-white hover:text-ink transition-colors">
                See upcoming programs <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
